import { formatCurrencyRound } from '@utils/formatters.util';
import { Tier } from './types';
import { DiscountProgressItem } from './DiscountProgressItem';
import cx from '@utils/cx.util';
import { useEffect, useMemo, useState } from 'react';

type Props = {
  currentProgress: number;
  tiers: Tier[];
  nextTierInfo: {
    threshold: number;
    amountNeeded: number;
    gift: string;
  } | null;
  currentSubtotal: number;
  className?: string;
  variant?: 'default' | 'transparent' | 'fixed-bottom';
};

export const TieredDiscountProgress = ({
  currentProgress,
  tiers,
  nextTierInfo,
  currentSubtotal,
  className = '',
  variant = 'default',
}: Props) => {
  const [lastTierThreshold, setLastTierThreshold] = useState(0);
  const [tierChangeCount, setTierChangeCount] = useState(0);

  useEffect(() => {
    if (
      tiers.length > 0 &&
      tiers[tiers.length - 1]?.threshold > 0 &&
      tiers[tiers.length - 1]?.threshold !== lastTierThreshold
    ) {
      setLastTierThreshold(tiers[tiers.length - 1]?.threshold);
      setTierChangeCount((prev) => prev + 1);
    }
  }, [tiers, lastTierThreshold]);

  const prevTier = useMemo(() => {
    return tiers.findLast((tier) => tier.threshold <= currentSubtotal);
  }, [tiers, currentSubtotal]);
  const isPrevTierFreeShipping = prevTier?.gift?.toLowerCase() === 'free shipping';

  return (
    <div
      className={cx(
        {
          'tw:rounded-button tw:bg-[#F8F8F8] tw:px-4 tw:py-2.5': variant === 'default',
          'tw:fixed tw:bottom-0 tw:left-0 tw:right-0 tw:z-[1003] tw:flex tw:flex-col tw:items-center tw:justify-center tw:bg-[#F8F8F8] tw:px-4 tw:py-3 tw:shadow-[-3px_-3px_6px_#00000029] tw:md:flex-row tw:md:gap-3 tw:md:py-4':
            variant === 'fixed-bottom',
        },
        className
      )}>
      <div
        className={cx({
          'tw:flex tw:w-full tw:shrink-0 tw:flex-col tw:mb-0 tw:md:w-auto tw:items-center tw:md:items-start':
            variant === 'fixed-bottom',
        })}>
        <Title
          prevTier={prevTier}
          nextTierInfo={nextTierInfo}
          isPrevTierFreeShipping={isPrevTierFreeShipping}
          tiers={tiers}
        />
        <Subtitle prevTier={prevTier} nextTierInfo={nextTierInfo} variant={variant} tiers={tiers} />
      </div>

      <div
        className={cx('tw:relative tw:mt-5 tw:w-full tw:px-2', {
          'tw:mx-auto tw:max-w-[400px] tw:md:mx-0 tw:md:max-w-[700px]': variant === 'fixed-bottom',
        })}>
        <div className='tw:relative tw:z-[1] tw:mx-6 tw:bg-[#d1d1d1]'>
          <div
            className='tw:h-[6px] tw:rounded-xl tw:bg-[#5F8047] tw:transition-all tw:duration-300'
            style={{ width: `${currentProgress}%` }}></div>
        </div>
        <div className='tw:-mx-5 tw:-mt-[18px] tw:overflow-hidden'>
          <div
            key={tiers[tiers.length - 1]?.threshold || 0}
            className={cx('tw:relative tw:z-[2] tw:flex tw:justify-between', {
              [`animate-tier-progress_${tiers.length}`]: lastTierThreshold && tierChangeCount > 1,
            })}>
            {tiers.map((tier) => (
              <DiscountProgressItem
                key={tier.threshold}
                {...tier}
                isActive={currentSubtotal >= tier.threshold}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const Title = ({
  prevTier,
  nextTierInfo,
  isPrevTierFreeShipping,
  tiers,
}: {
  prevTier: Tier | null;
  nextTierInfo: {
    threshold: number;
    amountNeeded: number;
    gift: string;
  } | null;
  isPrevTierFreeShipping: boolean;
  tiers: Tier[];
}) => {
  return (
    <p className='tw:mb-0 tw:text-base tw:font-bold tw:text-body'>
      {prevTier && prevTier.gift ? (
        <>
          You've earned{' '}
          <span className={cx({ 'tw:capitalize': isPrevTierFreeShipping })}>{prevTier?.gift}</span>
          {prevTier?.gift?.toLowerCase() !== 'free shipping' && ' your order'}!
        </>
      ) : nextTierInfo && nextTierInfo.gift ? (
        <>
          Spend {formatCurrencyRound(nextTierInfo.amountNeeded)} to earn{' '}
          <span className='tw:uppercase'>{nextTierInfo.gift || 'next discount'}</span>
        </>
      ) : (
        'Buy more & Save!'
      )}
    </p>
  );
};

const Subtitle = ({
  prevTier,
  nextTierInfo,
  variant,
  tiers,
}: {
  prevTier: Tier | null;
  nextTierInfo: {
    threshold: number;
    amountNeeded: number;
    gift: string;
  } | null;
  variant: 'default' | 'transparent' | 'fixed-bottom';
  tiers: Tier[];
}) => {
  if (!prevTier || !prevTier.gift || tiers.length === 0) return null;

  return (
    <>
      {nextTierInfo ? (
        <p
          className={cx('tw:font-normal tw:text-body', {
            'tw:mb-3 tw:text-sm': variant === 'default',
            'tw:mb-0 tw:text-base': variant === 'fixed-bottom',
          })}>
          Spend {formatCurrencyRound(nextTierInfo.amountNeeded)} to earn{' '}
          <span className='tw:lowercase'>{nextTierInfo.gift || 'next discount'}</span>
        </p>
      ) : (
        <p
          className={cx('tw:mb-3 tw:text-sm tw:font-normal tw:text-body', {
            'tw:mb-0 tw:text-base': variant === 'fixed-bottom',
          })}>
          All discounts are unlocked!
        </p>
      )}
    </>
  );
};
